import { useEffect, useRef, useState } from 'react';

export default function CTASection() {
  const [isVisible, setIsVisible] = useState(false);
  const [showHMAForm, setShowHMAForm] = useState(false);
  const [hmaSubmitted, setHmaSubmitted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleHMASubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    await fetch('https://readdy.ai/api/form/d79n9fj86jhav3jpfnjg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    });
    setHmaSubmitted(true);
  };

  const checks = [
    'No commitment required',
    '24-hour response',
    'Senior partner only',
    'Florida-specific insight',
  ];

  return (
    <>
      <section ref={ref} className="relative py-28 overflow-hidden bg-slate-950">
        {/* Subtle amber glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[400px] bg-amber-500/8 rounded-full blur-[140px] pointer-events-none"></div>

        {/* Grid texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(245,158,11,1) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 text-center">
          {/* Eyebrow */}
          <div className={`inline-flex items-center space-x-3 mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="h-px w-12 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.3em]">Ready to Partner?</span>
            <div className="h-px w-12 bg-amber-500"></div>
          </div>

          {/* Headline */}
          <div className={`mb-6 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-6xl lg:text-7xl font-extralight text-white leading-[1.05] tracking-tight">
              One Conversation.
            </h2>
            <h2 className="text-5xl lg:text-6xl font-extralight leading-[1.1] tracking-tight mt-2">
              <span className="text-amber-400 italic">A Clear Picture of What&apos;s Possible.</span>
            </h2>
          </div>

          {/* Subtext */}
          <p className={`text-lg text-gray-400 font-light max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            No pitch deck. No junior rep. A direct conversation with a 360 senior partner about what your hotel should be doing better — this week.
          </p>

          {/* Check marks */}
          <div className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {checks.map((check, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-amber-400 text-sm"></i>
                </div>
                <span className="text-sm text-gray-300 font-light whitespace-nowrap">{check}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-wrap items-center justify-center gap-3 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="group px-6 py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center space-x-2"
            >
              <span>Get a Free Assessment</span>
              <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
            </button>
            <button
              onClick={() => setShowHMAForm(true)}
              className="group px-6 py-3 rounded-full border border-white/20 text-white text-sm font-light tracking-wide hover:border-amber-500/50 hover:bg-white/5 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center space-x-2"
            >
              <span>Free HMA Review</span>
              <i className="ri-file-text-line text-base text-amber-400 transition-transform group-hover:scale-110"></i>
            </button>
          </div>
        </div>
      </section>

      {/* HMA Review Modal */}
      {showHMAForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-8 relative">
            <button
              onClick={() => { setShowHMAForm(false); setHmaSubmitted(false); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            {hmaSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-2">Request Received</h3>
                <p className="text-gray-500 font-light">We&apos;ll review your HMA and respond within 48 hours with our findings.</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-light text-gray-900 mb-1">Free HMA Review</h3>
                <p className="text-sm text-gray-500 font-light mb-6">Send us your management agreement details and we&apos;ll flag any issues within 48 hours.</p>
                <form data-readdy-form onSubmit={handleHMASubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">First Name</label>
                      <input name="first_name" required type="text" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="John" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Last Name</label>
                      <input name="last_name" required type="text" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="Smith" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Email</label>
                    <input name="email" required type="email" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="john@yourhotel.com" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Phone</label>
                    <input name="phone" type="tel" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="(321) 000-0000" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Property Name &amp; Location</label>
                    <input name="property" required type="text" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400" placeholder="e.g. Sunset Inn, Orlando FL" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Brief Description of Your HMA Concern</label>
                    <textarea
                      name="hma_concern"
                      maxLength={500}
                      rows={3}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400 resize-none"
                      placeholder="e.g. Renewal clause, fee structure, performance triggers..."
                    ></textarea>
                  </div>
                  <button type="submit" className="w-full py-3 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white font-medium text-sm tracking-wide transition-all whitespace-nowrap cursor-pointer">
                    Submit for Review
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
